import { useState, type ReactNode } from "react";
import { Dialog } from "@mui/material";
import { IconX } from "@tabler/icons-react";
import { cn } from "../../lib/utils";

type ImageLightboxProps = {
  src: string;
  alt: string;
  /** Classes for the inline (closed) image. The dialog sizes its own copy. */
  className?: string;
  /** Accessible name for the trigger, e.g. "Open the figure". */
  openLabel: string;
  closeLabel: string;
  /**
   * Rendered under the enlarged image. This is where content too long for the
   * page belongs — a publisher's figure legend runs to 392 characters at the
   * median and 1 279 at p90, which is a dialog's worth of text, not a caption's.
   * `null` when there is nothing to say beyond the image.
   */
  detail: ReactNode | null;
};

/**
 * An image that opens full-size on tap, with room for the text that could not
 * fit beside it.
 *
 * A UI primitive rather than a feature component, and that is what lets it own
 * `useState`: "which figure is open" is machinery, and pushing it up would put
 * state into renderers the house rules keep stateless. The trigger is a real
 * `<button>` — a click handler on an `<img>` is invisible to a keyboard and to
 * assistive tech, and a dense chart on a phone is exactly the case where the
 * reader who needs to enlarge it is least likely to be using a mouse.
 *
 * The button resets its own chrome (`appearance-none`, no padding or border):
 * this subtree is also snapshotted by the PNG export, and a default button
 * background would be baked into the exported poster.
 */
function ImageLightbox({
  src,
  alt,
  className,
  openLabel,
  closeLabel,
  detail,
}: ImageLightboxProps): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        aria-label={openLabel}
        onClick={() => {
          setIsOpen(true);
        }}
        className="block w-full cursor-zoom-in appearance-none border-0 bg-transparent p-0"
      >
        <img className={cn("w-full", className)} src={src} alt={alt} />
      </button>
      <Dialog
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        fullWidth
        maxWidth="lg"
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "var(--color-ds-white)",
            borderRadius: "16px",
            margin: "16px",
            width: "calc(100% - 32px)",
          },
        }}
      >
        <div className="relative p-4">
          <button
            type="button"
            aria-label={closeLabel}
            onClick={() => {
              setIsOpen(false);
            }}
            className="absolute right-4 top-4 grid size-9 place-items-center rounded-full border-0 bg-ds-white/90 p-0 text-ds-slate-950 shadow-md"
          >
            <IconX size={20} />
          </button>
          {/* The enlarged image is the point of the dialog, so it gets the
              viewport; the legend scrolls beneath it rather than competing. */}
          <img
            className="mx-auto max-h-[70dvh] w-auto max-w-full rounded-xl bg-white object-contain"
            src={src}
            alt={alt}
          />
          {detail === null ? null : (
            <div className="mt-4 max-h-[25dvh] overflow-y-auto">{detail}</div>
          )}
        </div>
      </Dialog>
    </>
  );
}

export { ImageLightbox, type ImageLightboxProps };
